/**
 * Per-booking "History" section for the booking detail modals — who created,
 * rescheduled, cancelled or checked in this booking, pulled from the venue
 * activity feed filtered to the booking (or recurring group).
 *
 * Renders nothing for staff without activity.view (the backend 403s the
 * endpoint), and stays quiet on errors — history is informational, it must
 * never break the modal.
 */
import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { vendorApi } from '../../services/api';
import { useVenueRole } from '../../hooks/useVenueRole';
import {
  ActivityEntry, ACTION_TYPES, actionIcon, actionChipClass, formatWhen,
} from '../../utils/activityHelpers';

interface BookingActivityTimelineProps {
  venueId: number;
  /** One-time / manual booking id */
  bookingId?: number;
  /** Customer recurring booking (group) id — includes its instances' events */
  recurringBookingId?: number;
  /** Manual recurring pack id — includes every session's events */
  recurringGroupId?: string;
}

export default function BookingActivityTimeline({
  venueId,
  bookingId,
  recurringBookingId,
  recurringGroupId,
}: BookingActivityTimelineProps) {
  const { can } = useVenueRole();
  const canView = can('activity.view');

  const [entries, setEntries] = useState<ActivityEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!canView || !venueId || (!bookingId && !recurringBookingId && !recurringGroupId)) return;

    let cancelled = false;
    setEntries(null);
    setFailed(false);

    vendorApi
      .getVenueActivity(venueId, {
        booking_id: bookingId,
        recurring_booking_id: recurringBookingId,
        recurring_group_id: recurringGroupId,
        per_page: 25,
      })
      .then((result) => {
        if (!cancelled) setEntries(result.data as ActivityEntry[]);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [canView, venueId, bookingId, recurringBookingId, recurringGroupId]);

  if (!canView || failed) return null;

  return (
    <div className="border-t border-gray-100 pt-4">
      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-gray-400" />
        History
      </h3>

      {entries === null ? (
        <div className="space-y-2">
          <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-1/2" />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-xs text-gray-400">No recorded activity for this booking yet.</p>
      ) : (
        <ul className="space-y-3">
          {entries.map((entry) => {
            const Icon = actionIcon(entry.action_type);
            return (
              <li key={entry.id} className="flex items-start gap-2.5">
                <div className={`mt-0.5 p-1.5 rounded-md shrink-0 ${actionChipClass(entry.action_type)}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-900">
                    {ACTION_TYPES[entry.action_type] ?? entry.action_type}
                    {entry.user && <span className="font-normal text-gray-500"> · by {entry.user.name}</span>}
                    <span className="font-normal text-gray-400"> · {formatWhen(entry.created_at)}</span>
                  </p>
                  <p className="text-xs text-gray-500 break-words">{entry.description}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
